import { useContext, useState } from 'react';

import Flatpickr from 'react-flatpickr';
import { Modal } from '../BaseModal';
import { CreateInterruptionsProps } from './CreateInterruptions.Props';

import { AppContext } from '../../../../common';
import { NewInterruptionModel, WorkshiftSchedulingModel } from '../../../../models';
import {
    covertFormatHour,
    formatDate,
    getEmployeesIds,
    handlerSetTime,
} from '../../../../utils';
import { InterruptionReferenceSelector } from '../../selectors/interruption/InterruptionReferenceSelector';
import { InterruptionSelector } from '../../selectors/interruption/InterruptionSelector';

export const CreateInterruptionsModel = ({
    setModalOpen,
    saveInterruption,
    isOpen,
    employee,
    startDate,
    endDate,
    workshifts,
}: CreateInterruptionsProps) => {
    const {
        interruptionTypesList,
        langResources,
        interruptionReferenceTypesList,
        interruptionTypesByUserList,
    } = useContext(AppContext);

    const [searchTerm, setSearchTerm] = useState('');
    const [searchReferenceTerm, setSearchReferenceTerm] = useState('');
    const [interruptionTypeId, setInterruptionTypeId] = useState<number>(0);
    const [referenceTypeId, setReferenceTypeId] = useState<number>(0);
    const [startHour, setStartHour] = useState('');
    const [endHour, setEndHour] = useState('');
    const [observations, setObservations] = useState('');
    const [isAdditionalHour, setIsAdditionalHour] = useState(false);
    const [isOnlyOvertime, setIsOnlyOvertime] = useState(false);

    const [isWholeWorkshift, setIsWholeWorkshift] = useState(false);
    const [isAllDay, setIsAllDay] = useState(false);
    const [canEditDate, setCanEditDate] = useState(false);

    const [isPaid, setIsPaid] = useState(false);
    const [isPaidDisabled, setIsPaidDisabled] = useState(false);
    const [isAutomaticDiscount, setIsAutomaticDiscount] = useState(false);
    const [isError, setError] = useState({ status: false, message: '' });

    const handleCreateNewInterruption = () => {
        if (
            (startDate !== '' && endDate !== '' && interruptionTypeId) ||
            isWholeWorkshift
        ) {
            setError({ status: false, message: '' });

            let newStartDate =
                formatDate(startDate) + 'T' + covertFormatHour(startHour);
            let newEnDate = formatDate(endDate) + 'T' + covertFormatHour(endHour);

            if (isWholeWorkshift && workshifts && workshifts.length >= 1) {
                newStartDate = formatDate(startDate) + 'T' + '00:00';
                newEnDate = formatDate(endDate) + 'T' + '00:00';
            }

            if (newStartDate >= newEnDate && !isWholeWorkshift) {
                setError({
                    status: true,
                    message: langResources['msg.validation.startDate'],
                });
                return;
            }

            if (newStartDate < newEnDate || isWholeWorkshift) {
                const body: NewInterruptionModel = {
                    employeeIds: getEmployeesIds(employee ? employee : []),
                    interruptionTypeId,
                    interruptionReferenceId: referenceTypeId,
                    isPaid,
                    isAutomaticDiscount: isAutomaticDiscount,
                    startDate: newStartDate,
                    endDate: newEnDate,
                    observations,
                    isAdditionalHour,
                    isOnlyOvertime,
                    isWholeWorkshift,
                    workshiftsIds: workshifts?.map((w) => w.id),
                };

                setModalOpen(false);
                saveInterruption(body);
            } else {
                setError({
                    status: true,
                    message: langResources['msg.validation.startDate'],
                });
            }
        } else {
            setError({
                status: true,
                message: langResources['create.interruption.Modal']['validation'],
            });
        }
    };

    const setHourByDay = (interruptionId?: number) => {
        const intId = interruptionId ? interruptionId : interruptionTypeId;

        const interruption = interruptionTypesList.find((i) => i.id == intId);

        let defaultInitialHourDay = '07:00';
        let defaultFinalHourDay = '15:00';

        if (interruption?.defaultTime) {
            let finalHourValue = new Date(interruption.defaultTime).getHours();
            let finalMinuteValue = new Date(interruption.defaultTime).getMinutes();

            if (finalHourValue == 0 && finalMinuteValue == 0) {
                setStartHour('00:00');
                setEndHour('00:00');
                setError({
                    message: langResources['msg.invalid.validation.defaultTime'],
                    status: true,
                });
            } else {
                const [horaInicialHoras, horaInicialMinutos] = defaultInitialHourDay
                    .split(':')
                    .map(Number);

                finalHourValue += horaInicialHoras;
                finalMinuteValue += horaInicialMinutos;

                const horaFinal = new Date(interruption.defaultTime);

                horaFinal.setHours(finalHourValue, finalMinuteValue);

                defaultFinalHourDay = horaFinal.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                });

                setStartHour(defaultInitialHourDay);
                setEndHour(defaultFinalHourDay);
            }
        } else {
            setStartHour('00:00');
            setEndHour('00:00');
            setError({
                message: langResources['msg.null.validation.defaultTime'],
                status: true,
            });
        }
    };

    const onChangeInterruptionTypes = (
        name: string,
        value: string | null | number | undefined
    ) => {
        const result = interruptionTypesList?.find(
            (interruption) => interruption.id === value
        );

        if (result) {
            setInterruptionTypeId(result.id);

            const paid = result.isPaid ?? false;
            setIsPaid(paid);

            setIsPaidDisabled(paid);

            if (isAllDay) {
                setHourByDay(result.id);
            }
        }
    };

    const onChangeInterruptionTypesReference = (
        name: string,
        value: string | null | number | undefined
    ) => {
        let result = interruptionReferenceTypesList?.find(
            (interruption) => interruption.id == value
        );

        if (result) {
            setReferenceTypeId(result.id);
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        window.localStorage.removeItem('selection-range');
    };

    const findFloatingWorkshift = (
        workshiftsList: WorkshiftSchedulingModel[] | undefined
    ) => {
        return workshiftsList?.map((w) => w.workshift?.isFloating);
    };

    const handlerShowDate = () => {
        let defaultInitialHourDay = workshifts?.at(0)?.startTime.split('T')[1];
        let defaultFinalHourDay = workshifts?.at(0)?.endTime.split('T')[1];

        if (defaultInitialHourDay && defaultFinalHourDay) {
            setStartHour(defaultInitialHourDay);
            setEndHour(defaultFinalHourDay);
        }
    };

    const handlerSetWholeWorkshift = () => {
        setIsWholeWorkshift(!isWholeWorkshift);
        setCanEditDate(!canEditDate);

        setStartHour('');
        setEndHour('');
        handlerShowDate();
    };

    const handlerSetWholeDay = () => {
        setIsAllDay(!isAllDay);
        setCanEditDate(!canEditDate);

        setStartHour('');
        setEndHour('');
        setHourByDay();
    };

    return (
        <Modal
            isOpen={isOpen}
            modalTitle={langResources['create.interruption.Modal']['title']}
            onClose={handleCloseModal}
            onSubmit={handleCreateNewInterruption}
            disableSubmit={
                interruptionTypeId === 0 ||
                startHour === '' ||
                endHour === '' ||
                isError.message !== ''
            }
        >
            <div className='modal-body text-black border rounded mb-2 row m-2 g-3'>
                <div className='col-12 col-md-6 '>
                    <div>
                        <InterruptionSelector
                            name=''
                            onChange={onChangeInterruptionTypes}
                            value={interruptionTypeId}
                            label={
                                langResources['create.interruption.Modal'][
                                    'select.interruptions'
                                ]
                            }
                        />
                    </div>
                </div>

                <div className='col-12 col-md-6 '>
                    <InterruptionReferenceSelector
                        name=''
                        onChange={onChangeInterruptionTypesReference}
                        value={referenceTypeId}
                        label={'Filtrar referencia de interrupción'}
                    />
                </div>

                <div className='col-12 col-md-6'>
                    <div className='d-flex justify-content-center gap-2'>
                        <div className='w-100'>
                            <label
                                htmlFor='startDate'
                                className='form-label d-block'
                            >
                                {
                                    langResources['create.interruption.Modal'][
                                        'starDate'
                                    ]
                                }
                            </label>
                            <Flatpickr
                                className='form-control text-black'
                                value={startHour}
                                disabled={canEditDate}
                                onChange={(e) => handlerSetTime(e, setStartHour)}
                                options={{
                                    enableTime: true,
                                    noCalendar: true,
                                    dateFormat: 'H:i',
                                }}
                            />
                        </div>

                        <div className='w-100'>
                            <label htmlFor='endDate' className='form-label d-block'>
                                {
                                    langResources['create.interruption.Modal'][
                                        'endDate'
                                    ]
                                }
                            </label>
                            <Flatpickr
                                className='form-control text-black'
                                options={{
                                    enableTime: true,
                                    noCalendar: true,
                                    dateFormat: 'H:i',
                                }}
                                disabled={canEditDate}
                                value={endHour}
                                onChange={(e) => handlerSetTime(e, setEndHour)}
                            />
                        </div>
                    </div>

                    <div className='form-check checked-blue my-3'>
                        <div className='d-flex gap-5'>
                            {workshifts &&
                                !findFloatingWorkshift(workshifts)?.includes(true) &&
                                workshifts?.length > 0 && (
                                    <div
                                        className={`form-check text-start p-0 checked-custom`}
                                    >
                                        <input
                                            className='form-check-input '
                                            type='checkbox'
                                            value='input-hours-workshift'
                                            id='input-hours-workshift'
                                            disabled={isAllDay}
                                            onChange={(e) => {
                                                handlerSetWholeWorkshift();
                                            }}
                                        />
                                        <label
                                            className='form-check-label'
                                            htmlFor='input-hours-workshift'
                                        >
                                            {
                                                langResources[
                                                    'create.interruption.Modal'
                                                ]['whole.workshift.scheduling.label']
                                            }
                                        </label>
                                    </div>
                                )}
                            <div
                                className={`form-check text-start p-0 checked-custom`}
                            >
                                <input
                                    className='form-check-input '
                                    type='checkbox'
                                    value='input-hours-workshift'
                                    id='input-hours-day'
                                    disabled={isWholeWorkshift}
                                    onChange={(e) => {
                                        handlerSetWholeDay();
                                    }}
                                />
                                <label
                                    className='form-check-label'
                                    htmlFor='input-hours-day'
                                >
                                    {
                                        langResources['create.interruption.Modal'][
                                            'whole.day.label'
                                        ]
                                    }
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='col-12 col-md-6'>
                    <div>
                        <div className={`form-check text-start checked-custom mb-3`}>
                            <input
                                className='form-check-input '
                                type='checkbox'
                                value='input-isAutomaticDiscount'
                                id='input-isAutomaticDiscount'
                                onChange={(e) =>
                                    setIsAutomaticDiscount(!isAutomaticDiscount)
                                }
                            />
                            <label
                                className='form-check-label'
                                htmlFor='input-isAutomaticDiscount'
                            >
                                {
                                    langResources['create.interruption.Modal'][
                                        'automatic.discount.label'
                                    ]
                                }
                            </label>
                        </div>

                        <div className={`form-check text-start checked-custom`}>
                            <input
                                className='form-check-input btn-primary2'
                                type='checkbox'
                                value={'input-paid'}
                                id='input-paid'
                                checked={isPaid}
                                disabled={isPaidDisabled}
                                onChange={(e) => setIsPaid(e.target.checked)}
                            />
                            <label className='form-check-label' htmlFor='input-paid'>
                                {
                                    langResources['create.interruption.Modal'][
                                        'paid.label'
                                    ]
                                }
                            </label>
                        </div>
                    </div>
                </div>

                <div className='col-12 mt-3'>
                    <div className='form-floating'>
                        <textarea
                            className='form-control text-black'
                            id='input-observations'
                            style={{
                                minHeight: 100,
                                maxHeight: '200px',
                                overflowY: 'scroll',
                            }}
                            value={observations}
                            onChange={(e) => setObservations(e.target.value)}
                        ></textarea>
                        <label htmlFor='input-observations'>
                            {
                                langResources['create.interruption.Modal'][
                                    'observations.label'
                                ]
                            }
                        </label>
                    </div>
                </div>

                {isError.status && (
                    <div className='alert alert-danger mt-4' role='alert'>
                        {isError.message}
                    </div>
                )}
            </div>
        </Modal>
    );
};
